import gsap from 'gsap'
import Node from 'nanogl-gltf/lib/elements/Node'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'

import Deferred from '@/webgl/core/Deferred'
import { clamp01, mix } from '@/webgl/math'
import IAnimationTrack from '@/webgl/runner/interfaces/IAnimationTrack'
import LanesRenderer from '@/webgl/runner/rendering/LanesRenderer'

type LaneRevealOptions = {
  reverse: boolean
  laneLength: number
  delay: number
  duration: number
}

export default class LanesRevealAnimation implements IAnimationTrack {
  laneSources: Node[] = []
  laneAnimated: Node[] = []
  _target: number = 0

  options: LaneRevealOptions

  constructor(
    private lanes: LanesRenderer,
    options: Partial<LaneRevealOptions> = {}
  ) {
    this.options = {
      delay: 0,
      laneLength: 10,
      duration: 2,
      reverse: false,
      ...options,
    }
  }

  init(): void {
    this.laneSources = [
      this.lanes.gltf.getElementByName(GltfTypes.NODE, 'lane_left'),
      this.lanes.gltf.getElementByName(GltfTypes.NODE, 'lane_right'),
      this.lanes.gltf.getElementByName(GltfTypes.NODE, 'lane_middle'),
    ]

    this.laneAnimated = [new Node(), new Node(), new Node()]

    for (let i = 0; i < this.laneSources.length; i++) {
      this.laneAnimated[i].add(this.laneSources[i])
      this.laneAnimated[i].z = -this.options.laneLength
      this.laneSources[i].z = this.options.laneLength
      this.lanes.node.add(this.laneAnimated[i])
    }

    const initial = this.options.reverse ? 1 : 0
    const target = this.options.reverse ? 0 : 1

    this.update(initial)
    this._target = target
  }

  cleanup() {
    for (let i = 0; i < this.laneSources.length; i++) {
      this.laneSources[i].z = 0
      this.lanes.node.remove(this.laneAnimated[i])
      this.lanes.node.add(this.laneSources[i])
    }
  }

  update = (p: number) => {
    const step = this.laneAnimated.length
    const stepSize = (1.0 / step) * 0.1

    for (let i = 0; i < this.laneAnimated.length; i++) {
      const node = this.laneAnimated[i]
      // const progress = clamp01(p - i * 0.1);
      // const progress = mix(0.0, 1.0, clamp01(p * (1.0 + stepSize * (step - 1)) * (1.0 - i * stepSize)));
      const progress = clamp01(mix(0.0, 1.0 + 0.1 * i, p))
      const sx = 0.5 + 0.5 * progress
      const sz = 0.0 + progress
      node.scale[0] = sx
      node.scale[2] = sz
      node.invalidate()
    }
  }

  play(): Promise<unknown> {
    const o = { progress: this._target == 0 ? 1 : 0 }
    const defer = new Deferred<void>()
    gsap.to(o, {
      progress: this._target,
      duration: this.options.duration,
      delay: this.options.delay,
      ease: 'power2.out',
      onUpdate: () => this.update(o.progress),
      onComplete: () => {
        this.cleanup()
        defer.resolve()
      },
    })
    return defer.promise
  }
}
